import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, RefreshCw, Search, ShieldCheck, Upload, X } from "lucide-react";
import { PROFESSION_CATEGORY_LABELS, type ProfessionCatalogEntry } from "../data/professionalTaxonomy";
import { api, type ProfessionalAdditionInput, type ProfessionalMemberRequest, type ProfessionalRequestSummary, type ProfessionalSubmissionInput } from "../lib/api";
import { Card, CardContent, CardHeader } from "./beui/SurfaceCard";
import { Input } from "./beui/Input";
import { Button } from "./beui/Button";
import { SubmissionDraftCard } from "./ProfessionalVerificationDraft";
import { RequestCard } from "./ProfessionalVerificationRequests";
import { type CredentialDraft, type Draft, MAX_CREDENTIALS_PER_REQUEST, MAX_EVIDENCE_IMAGES, PREVIEW_REQUESTS_ENABLED, PREVIEW_REQUESTS, PREVIEW_CREDENTIALS, makePreviewEvidenceBlob, findProfession, qualificationPathsFor, makeDraft, makeCredentialDraft, makeCredentialInput, isDraftReady, professionSuggestions } from "./professionalVerificationModel";

export default function ProfessionalVerificationSection({ identityApproved }: { identityApproved: boolean }) {
  const [requests, setRequests] = useState<ProfessionalRequestSummary[] | null>(null);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [requestDetail, setRequestDetail] = useState<ProfessionalMemberRequest | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [evidencePreview, setEvidencePreview] = useState<{ url: string; title: string } | null>(null);
  const [evidenceLoadingId, setEvidenceLoadingId] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    setRequestError(null);
    if (PREVIEW_REQUESTS_ENABLED) {
      setRequests(PREVIEW_REQUESTS);
      setLoadingRequests(false);
      return;
    }
    try {
      const response = await api.myProfessionalRequests();
      setRequests(response?.requests ?? []);
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : "Could not load Professional requests.");
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => { void loadRequests(); }, [loadRequests]);
  useEffect(() => () => {
    if (evidencePreview) URL.revokeObjectURL(evidencePreview.url);
  }, [evidencePreview]);

  const selectedTitles = useMemo(
    () => new Set(drafts.map((draft) => draft.professionTitle.trim().toLocaleLowerCase("en"))),
    [drafts],
  );
  const matches = useMemo(() => professionSuggestions(search, selectedTitles), [search, selectedTitles]);
  const exactMatch = useMemo(() => findProfession(search), [search]);
  const activeRequestCount = (requests ?? []).filter((request) => request.status === "pending" || request.status === "approved").length;
  const pendingNewDraftCount = drafts.filter((draft) => draft.mode === "new").length;
  const canAddNewProfession = activeRequestCount + pendingNewDraftCount < 10;
  const canAddCustom = search.trim().length >= 2 && search.trim().length <= 160 && !exactMatch
    && !selectedTitles.has(search.trim().toLocaleLowerCase("en")) && canAddNewProfession;
  const readyToSubmit = identityApproved && drafts.length > 0 && drafts.every(isDraftReady);

  function addProfession(title: string, entry: ProfessionCatalogEntry | null) {
    const normalized = title.trim().toLocaleLowerCase("en");
    if (!normalized || selectedTitles.has(normalized)) return;
    setDrafts((current) => [...current, makeDraft(title.trim(), entry)]);
    setSearch("");
    setSearchOpen(false);
    setSubmitError(null);
    setSubmitSuccess(null);
  }

  function updateDraft(id: string, update: Partial<Draft>) {
    setDrafts((current) => current.map((draft) => {
      if (draft.id !== id) return draft;
      const next = { ...draft, ...update };
      if (update.entry === undefined) return next;
      const paths = qualificationPathsFor(next.entry);
      // [verification.qualification-paths](../../../docs/product/profiles-and-verification.md#verification-qualification-paths): changing profession clears a disallowed path, proof type, and issuer instead of carrying mismatched claims forward.
      return {
        ...next,
        credentials: next.credentials.map((credential) => {
          if (!credential.qualificationPath || paths.includes(credential.qualificationPath)) {
            return { ...credential, qualificationPath: credential.qualificationPath ?? (next.entry && paths.length === 1 ? paths[0] : null) };
          }
          return { ...credential, qualificationPath: next.entry && paths.length === 1 ? paths[0] : null, credentialType: "", issuer: "" };
        }),
      };
    }));
  }

  function updateCredential(draftId: string, credentialId: string, update: Partial<CredentialDraft>) {
    setDrafts((current) => current.map((draft) => draft.id !== draftId ? draft : {
      ...draft,
      credentials: draft.credentials.map((credential) => credential.id === credentialId ? { ...credential, ...update } : credential),
    }));
  }

  function addCredentialEvidence(draftId: string, credentialId: string, files: File[]) {
    if (files.length === 0) return;
    // [verification.professional-evidence](../../../docs/product/profiles-and-verification.md#verification-professional-evidence): picker and drop events both append only the remaining image slots; the browser control is disabled when full.
    setDrafts((current) => current.map((draft) => draft.id !== draftId ? draft : {
      ...draft,
      credentials: draft.credentials.map((credential) => credential.id === credentialId
        ? { ...credential, evidence: [...credential.evidence, ...files.slice(0, Math.max(0, MAX_EVIDENCE_IMAGES - credential.evidence.length))] }
        : credential),
    }));
  }

  function addCredential(draftId: string) {
    setDrafts((current) => current.map((draft) => draft.id !== draftId || draft.credentials.length >= MAX_CREDENTIALS_PER_REQUEST
      ? draft
      : { ...draft, credentials: [...draft.credentials, makeCredentialDraft(draft.entry)] }));
  }

  function removeCredential(draftId: string, credentialId: string) {
    setDrafts((current) => current.map((draft) => draft.id !== draftId || draft.credentials.length <= 1
      ? draft
      : { ...draft, credentials: draft.credentials.filter((credential) => credential.id !== credentialId) }));
  }

  async function toggleRequest(summary: ProfessionalRequestSummary) {
    if (expandedRequestId === summary.id) {
      setExpandedRequestId(null);
      setRequestDetail(null);
      setDetailError(null);
      return;
    }
    setExpandedRequestId(summary.id);
    setRequestDetail(null);
    setDetailError(null);
    if (PREVIEW_REQUESTS_ENABLED) {
      setRequestDetail({ ...summary, credentials: PREVIEW_CREDENTIALS[summary.id] ?? [] });
      return;
    }
    setLoadingDetail(true);
    try {
      const response = await api.myProfessionalRequest(summary.id);
      setRequestDetail(response?.request ?? null);
    } catch (error) {
      setDetailError(error instanceof Error ? error.message : "Could not load this Professional request.");
    } finally {
      setLoadingDetail(false);
    }
  }

  async function startCorrection(summary: ProfessionalRequestSummary) {
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const response = await api.myProfessionalRequest(summary.id);
      const detail = response?.request;
      if (!detail) throw new Error("Could not load this Professional request.");
      const prior = detail.credentials[detail.credentials.length - 1];
      setDrafts((current) => [
        ...current,
        makeDraft(detail.professionTitle, findProfession(detail.professionTitle), "correction", detail.id, prior),
      ]);
      setExpandedRequestId(detail.id);
      setRequestDetail(detail);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Could not load this correction.");
    }
  }

  function startAddition(summary: ProfessionalRequestSummary) {
    setSubmitError(null);
    setSubmitSuccess(null);
    const entry = findProfession(summary.professionTitle);
    setDrafts((current) => [...current, makeDraft(summary.professionTitle, entry, "addition", summary.id)]);
  }

  async function submitDrafts() {
    if (!readyToSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    const completed = new Set<string>();
    const failures: string[] = [];
    // [verification.profession-requests](../../../docs/product/profiles-and-verification.md#verification-profession-requests): each profession is a separate request, so one failure must not discard successful submissions or unsent drafts.
    for (const draft of drafts) {
      const credentials = draft.credentials.map(makeCredentialInput);
      try {
        if (draft.mode === "addition" && draft.requestId) {
          const input: ProfessionalAdditionInput = { credentials };
          await api.addProfessionalCredentials(draft.requestId, input);
        } else {
          const input: ProfessionalSubmissionInput = {
            professionTitle: draft.professionTitle,
            credentials,
          };
          if (draft.mode === "correction" && draft.requestId) {
            await api.correctProfessionalRequest(draft.requestId, input);
          } else {
            await api.submitProfessionalRequest(input);
          }
        }
        completed.add(draft.id);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Could not submit this request.";
        failures.push(`${draft.professionTitle}: ${message}`);
      }
    }
    setDrafts((current) => current.filter((draft) => !completed.has(draft.id)));
    if (completed.size > 0) {
      const titles = drafts.filter((draft) => completed.has(draft.id)).map((draft) => draft.professionTitle);
      setSubmitSuccess(`Submitted separate requests for ${titles.join(" and ")}.`);
      await loadRequests();
    }
    if (failures.length > 0) setSubmitError(failures.join(" "));
    setSubmitting(false);
  }

  async function viewEvidence(requestId: string, imageId: string, title: string) {
    setEvidenceLoadingId(imageId);
    setSubmitError(null);
    try {
      const blob = PREVIEW_REQUESTS_ENABLED && requestId.startsWith("preview-")
        ? await makePreviewEvidenceBlob()
        : await api.professionalEvidenceBlob(requestId, imageId);
      setEvidencePreview((current) => {
        if (current) URL.revokeObjectURL(current.url);
        return { url: URL.createObjectURL(blob), title };
      });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Could not open private evidence.");
    } finally {
      setEvidenceLoadingId(null);
    }
  }

  return (
    <>
    <section id="professional-verification" className="verification-profession" aria-labelledby="professional-verification-heading">
      <Card className="h-full gap-0">
        <CardHeader className="flex flex-row items-start gap-3">
          <div className="verification-section-icon bg-navy-50 text-brand-red"><ShieldCheck aria-hidden="true" /></div>
          <h2 id="professional-verification-heading" className="min-w-0 flex-1 font-bold text-navy">Professional verification</h2>
          <span className="rounded-full bg-navy-50 px-2.5 py-1 text-xs font-medium text-navy-600">Optional</span>
        </CardHeader>
        <CardContent className="pt-5">

        {!identityApproved && <p className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" role="status">
          Verify your identity above before submitting a Professional request. You can explore professions and prepare evidence in the meantime.
        </p>}

        <div className={identityApproved ? "relative" : "relative mt-5"} onFocus={() => setSearchOpen(true)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setSearchOpen(false); }}>
            <Input
              id="professional-search"
              label="Find your profession"
              leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
              type="search"
              maxLength={160}
              value={search}
              onChange={value => { setSearch(value); setSearchOpen(true); }}
              onKeyDown={(event) => {
                if (event.key === "Escape") { setSearchOpen(false); return; }
                if (event.key !== "Enter") return;
                event.preventDefault();
                if (exactMatch && !selectedTitles.has(exactMatch.profession.toLocaleLowerCase("en")) && canAddNewProfession) addProfession(exactMatch.profession, exactMatch);
                else if (canAddCustom) addProfession(search.trim(), null);
              }}
              placeholder="Search or type a custom title"
              autoComplete="off"
            />
          {searchOpen && (
            <div className="profession-suggestions" role="listbox" aria-label="Profession suggestions">
              {matches.map((entry) => {
                const alreadySelected = selectedTitles.has(entry.profession.toLocaleLowerCase("en"));
                const qualificationPaths = Array.from(new Set(entry.classifications.map((classification) => PROFESSION_CATEGORY_LABELS[classification.category])));
                return (
                  <button key={entry.id} type="button" onClick={() => addProfession(entry.profession, entry)} disabled={alreadySelected || !canAddNewProfession} className="block w-full p-3 text-left transition-colors hover:bg-navy-50 focus-visible:bg-navy-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy-500 disabled:cursor-default disabled:opacity-50">
                    <span className="text-sm text-navy">
                      <span className="font-semibold">{entry.profession}</span>
                      <span className="text-mut"> · {qualificationPaths.join(" / ")}</span>
                    </span>
                    {alreadySelected && <span className="ml-2 text-xs text-mut">Selected</span>}
                  </button>
                );
              })}
              {canAddCustom && (
                <button type="button" onClick={() => addProfession(search.trim(), null)} className="block w-full p-3 text-left text-sm text-navy transition-colors hover:bg-navy-50 focus-visible:bg-navy-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy-500">
                  Other: <span className="font-semibold">{search.trim()}</span><span className="ml-2 text-xs text-mut">Use custom profession</span>
                </button>
              )}
              {matches.length === 0 && !canAddCustom && <p className="p-3 text-sm text-mut">Enter a custom profession title with 2–160 characters.</p>}
            </div>
          )}
        </div>

        {drafts.length > 0 && (
          <div className="mt-5 space-y-3">
            {drafts.map((draft) => (
              <SubmissionDraftCard
                key={draft.id}
                draft={draft}
                submitting={submitting}
                onChangeTitle={updateDraft}
                onChangeCredential={updateCredential}
                onAddEvidence={addCredentialEvidence}
                onAddCredential={addCredential}
                onRemoveCredential={removeCredential}
                onRemoveDraft={(id) => setDrafts((current) => current.filter((item) => item.id !== id))}
              />
            ))}
            <button type="button" onClick={() => void submitDrafts()} disabled={!readyToSubmit || submitting} className="btn-red w-full disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Submit for review{drafts.length > 1 ? ` (${drafts.length} professions)` : ""}
            </button>
          </div>
        )}

        {submitSuccess && <p role="status" className="mt-3 text-sm text-brand-green flex gap-2 items-center"><CheckCircle2 className="w-4 h-4" />{submitSuccess}</p>}
        {submitError && <p role="alert" className="mt-3 text-sm text-brand-red flex gap-2 items-center"><AlertCircle className="w-4 h-4" />{submitError}</p>}
        </CardContent>
      </Card>
    </section>

    <section className="verification-requests" aria-labelledby="professional-requests-heading">
      <Card className="h-full gap-0">
        <CardHeader>
          {PREVIEW_REQUESTS_ENABLED && <p className="mb-3 rounded-lg bg-blue-50 px-3 py-2 text-xs font-medium text-blue-800">Sample requests for local preview. These are not saved.</p>}
          <div className="flex items-center justify-between gap-3">
            <h2 id="professional-requests-heading" className="font-bold text-navy">Your profession requests</h2>
            <Button type="button" variant="ghost" size="icon" aria-label="Refresh status" title="Refresh status" onClick={() => void loadRequests()} disabled={loadingRequests} className="h-11 w-11 shrink-0">
              <RefreshCw aria-hidden="true" className={`h-4 w-4 ${loadingRequests ? "motion-safe:animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-3">
          {requestError && <p role="alert" className="mt-2 text-sm text-brand-red">{requestError}</p>}
          {loadingRequests && <p className="mt-2 text-sm text-mut">Loading your requests…</p>}
          {!loadingRequests && requests?.length === 0 && !requestError && <p className="verification-empty text-sm text-mut">No Professional requests yet.</p>}
          <div className="mt-3 space-y-2">
            {requests?.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                expanded={expandedRequestId === request.id}
                detail={expandedRequestId === request.id ? requestDetail : null}
                loadingDetail={expandedRequestId === request.id && loadingDetail}
                detailError={expandedRequestId === request.id ? detailError : null}
                evidenceLoadingId={evidenceLoadingId}
                onToggle={() => void toggleRequest(request)}
                onCorrect={() => void startCorrection(request)}
                onAddCredential={() => startAddition(request)}
                onViewEvidence={(imageId, title) => void viewEvidence(request.id, imageId, title)}
                preview={PREVIEW_REQUESTS_ENABLED}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </section>

      {evidencePreview && (
        <div className="fixed inset-0 z-[120] bg-black/80 p-4 grid place-items-center" role="dialog" aria-modal="true" aria-label={evidencePreview.title}>
          <div className="relative max-w-5xl max-h-full bg-white rounded-xl p-3">
            <button type="button" aria-label="Close private evidence preview" onClick={() => setEvidencePreview(null)} className="absolute right-2 top-2 rounded-full bg-white shadow p-2">
              <X className="w-4 h-4" />
            </button>
            <img src={evidencePreview.url} alt={evidencePreview.title} className="max-w-full max-h-[85vh] object-contain" />
          </div>
        </div>
      )}
    </>
  );
}
